import { Dispatch, Fragment, SetStateAction } from 'react'
import { Listbox, Transition } from '@headlessui/react'
import Icon from '../Icon'
import classNames from 'classnames'
import { twMerge } from 'tailwind-merge'

export interface Option {
	label?: string
	value?: string | number
	color?: string
}

interface SelectProps {
	options: Array<Option>
	classNameButton?: string
	classNameOptions?: string
	placeholder?: string
	selectedField: {
		label?: string
		value?: string
		color?: string
	}
	disabled?: boolean
	resetOnClickSelected?: boolean
	onChange?: (option: Option) => void
	setSelectedField: Dispatch<
		SetStateAction<{ label?: string; value?: string; color?: string }>
	>
}

export default function Select({
	options,
	classNameButton,
	classNameOptions,
	placeholder,
	selectedField,
	setSelectedField,
	onChange,
	disabled,
	resetOnClickSelected,
}: SelectProps) {
	return (
		<div className='flex w-full'>
			<Listbox
				value={selectedField}
				onChange={(value) => {
					onChange && onChange(value)

					if (resetOnClickSelected) {
						return selectedField?.value === value?.value
							? setSelectedField({})
							: setSelectedField(value)
					}

					return setSelectedField(value)
				}}
			>
				{({ open }) => (
					<div className='relative w-full'>
						<Listbox.Button
							className={twMerge(
								'relative w-full border border-gray-150 cursor-pointer rounded-md bg-transparent py-0 px-5 text-left dark:border-white/20 focus:outline-none focus-visible:ring-offset-2 sm:text-sm',
								classNames(classNameButton, {
									'dark:border-white/40 border-gray-500/40': open,
									'pointer-events-none opacity-40': disabled,
								})
							)}
						>
							<span
								className={twMerge(
									'flex truncate text-white items-center gap-2',
									classNames({
										'text-white': !selectedField.value,
									})
								)}
							>
								{selectedField?.color && (
									<Icon
										name='circle'
										width='12px'
										height='12px'
										className={twMerge('w-3 h-3', selectedField.color)}
									/>
								)}
								{selectedField?.label ||
									placeholder ||
									'Selecione uma das opções'}
							</span>
							<span className='pointer-events-none absolute inset-y-0 right-0 flex items-center -mt-[1px] pr-4'>
								<Icon
									name='chevron-down'
									fill='none'
									stroke='rgb(132 147 172)'
									width='16px'
									height='16px'
									className={twMerge(
										'transition-all duration-500',
										classNames({
											'strokebrand-primary rotate-180 dark:strokebrand-primary]':
												open,
										})
									)}
								/>
							</span>
						</Listbox.Button>
						<Transition
							as={Fragment}
							leave='transition ease-in duration-100'
							leaveFrom='opacity-100'
							leaveTo='opacity-0'
						>
							<Listbox.Options
								className={twMerge(
									'absolute flex mt-3 max-h-60 w-full overflow-clip rounded-sm bg-zinc-900 dark:border dark:border-white/5 text-base shadow-md ring-1 ring-[#000000]/5 focus:outline-none sm:text-sm',
									classNameOptions
								)}
							>
								<div
									id='sidebar-content'
									className='flex flex-col flex-grow relative overflow-auto max-h-full'
								>
									{options?.map((item, idx) => (
										<Listbox.Option
											key={idx}
											className={({ active, selected }) =>
												twMerge(
													'relative select-none py-[10px] pl-5 pr-4 transition-colors cursor-pointer',
													classNames('text-white/80', {
														'bg-brand-primary/5 text-brand-primary dark:bg-white/5 dark:text-white':
															active || selected,
													})
												)
											}
											value={item}
										>
											<span className='truncate text-sm font-medium flex items-center gap-2'>
												{item?.color && (
													<Icon
														name='circle'
														width='12px'
														height='12px'
														className={twMerge('w-3 h-3', item.color)}
													/>
												)}
												{item.label}
											</span>
										</Listbox.Option>
									))}
								</div>
							</Listbox.Options>
						</Transition>
					</div>
				)}
			</Listbox>
		</div>
	)
}
